<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';

// Components
import DeviceFrame from './parts/DeviceFrame.vue';
import HomeState from './parts/HomeState.vue';
import InsightModal from './parts/InsightModal.vue';
import Keyboard from './parts/Keyboard.vue';
import BetCardState from './parts/BetCardState.vue';
import SharePreviewState from './parts/SharePreviewState.vue';
import NativeShareState from './parts/NativeShareState.vue';
import demoShareChalk from '@/assets/images/demo-share-chalk.png';
import demoShareImg from '@/assets/images/demo-share.png';

const step = ref(0); 
const typedText = ref("");
const showInsightModal = ref(false);
const showSharePreview = ref(false);
const showNativeShare = ref(false);
const showKeyboard = ref(false);
const activeKey = ref("");
const previewIndex = ref(1);
const previewImage = ref(demoShareImg);

const tapState = ref({ x: 0, y: 0, active: false });
const showcaseRef = ref<HTMLElement | null>(null);

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const currentTime = computed(() => {
  if (step.value === 0) return '12:49';
  if (step.value === 2 && !showSharePreview.value) return '12:51';
  return '12:52';
});

const simulateTap = async (x: number, y: number) => {
  tapState.value = { x, y, active: true };
  await wait(400);
  tapState.value.active = false;
  await wait(200);
};

const typeInModal = async (text: string, sessionId: number) => {
  typedText.value = "";
  showKeyboard.value = true;
  await wait(400);
  if (sessionId !== currentSessionId.value) return;

  for (let i = 0; i < text.length; i++) {
    if (sessionId !== currentSessionId.value) {
      showKeyboard.value = false;
      return;
    }
    const char = text.charAt(i).toUpperCase();
    if (char !== " ") activeKey.value = char;
    typedText.value += text.charAt(i);
    await wait(60 + Math.random() * 40);
    activeKey.value = "";
    await wait(40);
  }
  
  if (sessionId !== currentSessionId.value) return;
  await wait(1200);
  if (sessionId !== currentSessionId.value) return;
  showKeyboard.value = false;
  await wait(300);
};

const emit = defineEmits(['stage-change']);

const isRunning = ref(false);
const currentSessionId = ref(0);

const runDemo = async () => {
  if (isRunning.value) return;
  isRunning.value = true;
  const sessionId = ++currentSessionId.value;

  const check = () => sessionId === currentSessionId.value;

  // 0. Reset
  step.value = 0; typedText.value = ""; 
  showInsightModal.value = false; showSharePreview.value = false; showNativeShare.value = false; showKeyboard.value = false;
  previewIndex.value = 1; previewImage.value = demoShareImg;
  emit('stage-change', 'reset');
  await wait(2000);
  if (!check()) return;

  // 1. Home -> Insight
  await simulateTap(160, 115); 
  if (!check()) return;
  await wait(400);
  showInsightModal.value = true;
  await wait(1000);
  if (!check()) return;

  // 2. Typing
  emit('stage-change', 'typing');
  // Pass check to typeInModal
  await typeInModal("Arsenal Liverpool", sessionId);
  if (!check()) return;
  
  // 3. Insight -> Bet Result
  await simulateTap(270, 75); 
  if (!check()) return;
  await wait(400);
  showInsightModal.value = false;
  step.value = 2;
  await wait(3000);
  if (!check()) return;

  // 4. Bet Result -> Share Preview
  await simulateTap(255, 615); 
  if (!check()) return;
  await wait(400);
  emit('stage-change', 'see-it');
  showSharePreview.value = true;
  await wait(1500);
  if (!check()) return;

  // 4b. Navigate Templates
  await simulateTap(120, 580);
  if (!check()) return;
  previewIndex.value = 2;
  previewImage.value = demoShareChalk;
  await wait(3000);
  if (!check()) return;

  // 5. Share Preview -> Native Share
  await simulateTap(260, 580); 
  if (!check()) return;
  await wait(400);
  emit('stage-change', 'share-it');
  showNativeShare.value = true;
  await wait(3000);
  if (!check()) return;
  
  emit('stage-change', 'completed');
  isRunning.value = false;
};

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    if (entries[0]?.isIntersecting) {
      runDemo();
      observer.disconnect();
    }
  }, { threshold: 0.3 });
  if (showcaseRef.value) observer.observe(showcaseRef.value);
});

const restart = () => {
  isRunning.value = false;
  runDemo();
};

const jumpToStage = async (stageId: string) => {
  isRunning.value = false;
  const sessionId = ++currentSessionId.value;
  const check = () => sessionId === currentSessionId.value;
  
  // Reset everything first
  step.value = 0; typedText.value = ""; 
  showInsightModal.value = false; showSharePreview.value = false; showNativeShare.value = false; showKeyboard.value = false;
  previewIndex.value = 1; previewImage.value = demoShareImg;
  
  if (stageId === 'typing') {
    // 1. Home -> Insight
    await simulateTap(160, 115); 
    if (!check()) return;
    showInsightModal.value = true;
    await wait(600);
    if (!check()) return;
    
    // 2. Typing
    emit('stage-change', 'typing');
    await typeInModal("Arsenal Liverpool", sessionId);
    if (!check()) return;
    
    // 3. Insight -> Bet Result
    await simulateTap(270, 75); 
    if (!check()) return;
    await wait(400);
    showInsightModal.value = false;
    step.value = 2;
    
  } else if (stageId === 'see-it') {
    step.value = 2;
    showSharePreview.value = true;
    emit('stage-change', 'see-it');
    await wait(1000);
    if (!check()) return;
    
    // Simulate navigation click
    await simulateTap(120, 580);
    if (!check()) return;
    previewIndex.value = 2;
    previewImage.value = demoShareChalk;
    
  } else if (stageId === 'share-it') {
    step.value = 2;
    showSharePreview.value = true;
    previewIndex.value = 2;
    previewImage.value = demoShareChalk;
    emit('stage-change', 'share-it');
    await wait(800);
    if (!check()) return;

    // Simulate click on Share pill button
    await simulateTap(260, 580);
    if (!check()) return;
    showNativeShare.value = true;
  }
};

defineExpose({ restart, jumpToStage });
</script>

<template>
  <div class="showcase-outer" ref="showcaseRef">
    <DeviceFrame :time="currentTime" :tapState="tapState">
      
      <!-- States (Non-interactive for the user, only automated) -->
      <div class="demo-pointer-mask">
        <HomeState v-if="step === 0 && !showInsightModal && !showSharePreview" />
      
      <transition name="fade">
        <InsightModal v-if="showInsightModal" :typedText="typedText" />
      </transition>

      <BetCardState 
        v-if="step === 2 && !showInsightModal && !showSharePreview" 
        :showInsightModal="showInsightModal"
        :showSharePreview="showSharePreview"
      />

      <transition name="fade">
        <SharePreviewState 
          v-if="showSharePreview" 
          :showSharePreview="showSharePreview" 
          :currentIndex="previewIndex"
          :currentImage="previewImage"
        />
      </transition>

      <!-- Overlays -->
      <transition name="slide-up">
        <Keyboard v-if="showKeyboard" :activeKey="activeKey" />
      </transition>

      <transition name="slide-up">
        <NativeShareState 
          v-if="showNativeShare" 
          :thumbImage="previewImage" 
          :autoScroll="true"
          @close="showNativeShare = false" 
        />
      </transition>
      </div>

    </DeviceFrame>
  </div>
</template>

<style scoped>
.showcase-outer { display: flex; flex-direction: column; align-items: center; gap: 24px; padding: 20px; }

.demo-pointer-mask {
  width: 100%; height: 100%;
  pointer-events: none; /* Disable user interaction with the demo */
  user-select: none;
  display: flex; flex-direction: column;
}
</style>
